import { useApi } from '@src/app/context/Api';
import { logger } from '@src/shared/includes/logger';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { useCallback } from 'react';

import modes from './includes/modes.json';

/**
 * useSaveCommonCriteria
 *
 * Makes an API request to save the common criteria.
 *
 * @param mode
 * @returns {(function({}=, function()=): Promise<void>)|*}
 */
export const useSaveCommonCriteria = (mode = null) => {
  const currentMode = modes?.[mode];

  const { clearApiCache, useApiMutation } = useApi('Criteria');
  const [SaveCommonCriteria] = useApiMutation('SaveCommonCriteria');

  const { toast } = useToast();

  return useCallback(async (submittedValues = {}, refetch = () => {}) => {
    try {
      const toastId = `common-criteria-${mode}`;

      toast.show({
        autoClose: false,
        id: `${toastId}-info`,
        isLoading: true,
        message: currentMode?.info?.message,
        title: currentMode?.info?.title,
        variant: 'info',
      });

      const payload = {
        ...submittedValues,
        ...{
          action: mode,
        },
      };

      await SaveCommonCriteria(payload, {
        onError: () => {
          toast.hide(`${toastId}-info`);
          toast.show({
            id: `${toastId}-error`,
            message: currentMode?.error?.message,
            title: currentMode?.error?.title,
            variant: 'error',
          });
        },
        onSuccess: async () => {
          toast.hide(`${toastId}-info`);
          toast.show({
            id: `${toastId}-success`,
            message: currentMode?.success?.message,
            title: currentMode?.success?.title,
            variant: 'success',
          });
          await clearApiCache([
            { apiKey: 'Criteria', queryKeys: ['ListCommonCriteria', 'ListCommonCriteriaVersions'] },
          ]);
          refetch();
        },
      });

      await clearApiCache([
        { apiKey: 'ActionPath', queryKeys: ['ListActionPaths', 'GetActionPath'] },
        { apiKey: 'Criteria', queryKeys: ['ListCommonCriteria', 'GetCommonCriteria', 'GetCommonCriteriaVersion'] },
      ]);
    } catch (error) {
      logger.error('src/entities/Criteria/ui/hooks/useSaveCommonCriteria/useSaveCommonCriteria.js', false, error);
    }
  }, []);
};
